import React from 'react'
import { Badge, Button, Empty, Spin, Tabs, Typography } from 'antd'
import { ArrowLeftOutlined } from '@ant-design/icons'
import chatArrowDownIcon from 'assets/icons/chat-arrow-down.svg'
import { chatApi } from 'api/chat'
import { ESupportChatTab, IChatMessage } from 'models/chat'
import { ISeller } from 'models/seller'
import { useChatScroll } from 'shared/hooks/useChatScroll'
import { useIsMobile } from 'shared/hooks/useIsMobile'
import { useNow } from 'shared/hooks/useNow'
import { useViewportFill } from 'shared/hooks/useViewportFill'
import { useAppDispatch, useAppSelector } from 'shared/hooks/useReduxHooks'
import { showMessage } from 'shared/utils/showMessage'
import { appSelectors } from 'store/app'
import {
  getMoreSupportChats,
  getSupportChat,
  getSupportChats,
  getSupportCounters,
  getSupportMessages,
  supportActions,
  supportSelectors
} from 'store/support'
import { ChatComposer } from 'components/chats/ChatComposer'
import { ChatMessageMedia } from 'components/chats/ChatMessageMedia'
import { ChatMessageMeta } from 'components/chats/ChatMessageMeta'
import { useChatAttachments } from 'components/chats/useChatAttachments'
import {
  formatDateTime,
  getChatAuthorName,
  getMessagePreview,
  hasMedia,
  getRequestErrorText,
  getSupportChatSubtitle,
  isWithinEditWindow
} from 'components/chats/utils'
import { CardLink } from 'components/ui/CardLink/CardLink'
import { getSellerEditPath } from 'routing/utils/getSellerEditPath'
import { getUserEditPath } from 'routing/utils/getUserEditPath'
import styles from 'components/chats/ChatsPage.module.scss'
import { SupportSellerPicker } from './SupportSellerPicker'

const { Text, Title } = Typography
const MESSAGE_MAX_LENGTH = 3000
const SCROLL_LOCK_CLASS = 'chats-scroll-lock'

export const Support: React.FC = () => {
  const dispatch = useAppDispatch()
  const isMobile = useIsMobile()
  const tab = useAppSelector(supportSelectors.selectTab)
  const chats = useAppSelector(supportSelectors.selectChats)
  const activeChat = useAppSelector(supportSelectors.selectActiveChat)
  const messages = useAppSelector(supportSelectors.selectMessages)
  const counters = useAppSelector(supportSelectors.selectCounters)
  const hasMoreChats = useAppSelector(supportSelectors.selectHasMoreChats)
  const isChatsLoading = useAppSelector(supportSelectors.selectIsChatsLoading)
  const isMessagesLoading = useAppSelector(supportSelectors.selectIsMessagesLoading)
  const userId = useAppSelector(appSelectors.selectUserId)
  const now = useNow(30000)

  const wrapperRef = React.useRef<HTMLDivElement>(null)
  useViewportFill(wrapperRef)
  const listItemsRef = React.useRef<HTMLDivElement | null>(null)
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null)
  const [messageText, setMessageText] = React.useState('')
  const [isSending, setIsSending] = React.useState(false)
  const [isMoreChatsLoading, setIsMoreChatsLoading] = React.useState(false)
  const [editingMessage, setEditingMessage] = React.useState<IChatMessage | null>(null)
  // Недописанный ответ: на время правки поле занято текстом сообщения
  const draftRef = React.useRef('')
  // Продавец, которому оператор пишет первым: обращения ещё нет
  const [draftSeller, setDraftSeller] = React.useState<ISeller | null>(null)
  const attachments = useChatAttachments(activeChat?._id || draftSeller?._id || null)

  const isOwnMessage = React.useCallback(
    (message: IChatMessage) => message.sender_type === 'SUPPORT',
    []
  )
  const {
    containerRef: messagesRef,
    newItemsCount: newMessagesCount,
    handleScroll: handleMessagesScroll,
    scrollToLatestItem: scrollToLatestMessage
  } = useChatScroll<IChatMessage>({
    items: messages,
    resetKey: activeChat?._id,
    isOwnItem: isOwnMessage
  })

  React.useEffect(() => {
    dispatch(getSupportChats(tab))
    dispatch(getSupportCounters())
  }, [dispatch, tab])

  // Класс вешаем на html: страница чатов на телефоне занимает ровно экран,
  // и прокрутка должна работать только внутри переписки
  React.useEffect(() => {
    document.documentElement.classList.add(SCROLL_LOCK_CLASS)

    return () => {
      document.documentElement.classList.remove(SCROLL_LOCK_CLASS)
      dispatch(supportActions.clearSupportState())
    }
  }, [dispatch])

  const loadMoreChats = React.useCallback(async () => {
    if (isMoreChatsLoading || !hasMoreChats) return

    setIsMoreChatsLoading(true)
    try {
      await dispatch(getMoreSupportChats())
    } finally {
      setIsMoreChatsLoading(false)
    }
  }, [dispatch, hasMoreChats, isMoreChatsLoading])

  React.useEffect(() => {
    const root = listItemsRef.current
    const target = loadMoreRef.current
    if (!root || !target || !hasMoreChats || isMoreChatsLoading || isChatsLoading) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          loadMoreChats()
        }
      },
      { root, rootMargin: '120px' }
    )

    observer.observe(target)

    return () => observer.disconnect()
  }, [chats.length, hasMoreChats, isChatsLoading, isMoreChatsLoading, loadMoreChats])

  const resetComposer = () => {
    setEditingMessage(null)
    draftRef.current = ''
    setMessageText('')
  }

  const handleSelectChat = (chatId: string) => {
    resetComposer()
    setDraftSeller(null)
    dispatch(supportActions.setMessages([]))
    dispatch(getSupportChat(chatId))
    dispatch(getSupportMessages(chatId))
  }

  // Чат продавца открываем мимо списка: заведённого рассылкой обращения в нём нет.
  // Нет обращения — открываем черновик, сервер заведёт его первым сообщением
  const handleOpenSellerChat = async (seller: ISeller) => {
    try {
      const { data } = await chatApi.getSupportChatWithSeller(seller._id)
      resetComposer()
      dispatch(supportActions.setMessages([]))
      dispatch(supportActions.setActiveChat(data.chat))
      setDraftSeller(data.chat ? null : seller)
      if (data.chat) {
        dispatch(getSupportMessages(data.chat._id))
      }
    } catch (e) {
      showMessage('error', 'Не удалось открыть переписку', getRequestErrorText(e))
    }
  }

  const handleMarkRead = async () => {
    if (!activeChat) return
    const chatId = activeChat._id

    try {
      await chatApi.markSupportRead(chatId)
      dispatch(supportActions.markChatRead(chatId))
      dispatch(getSupportCounters())
    } catch (e) {
      showMessage('error', 'Не удалось пометить прочитанным', getRequestErrorText(e))
    }
  }

  const handleMarkUnread = async () => {
    if (!activeChat) return
    const chatId = activeChat._id

    try {
      const { data } = await chatApi.markSupportUnread(chatId)
      dispatch(supportActions.applyChatUpdated({ chat: data.chat }))
      dispatch(supportActions.applyMessageUpdated({ chatId, message: data.message }))
      dispatch(getSupportCounters())
    } catch (e) {
      showMessage('error', 'Не удалось пометить непрочитанным', getRequestErrorText(e))
    }
  }

  const handleBackToList = () => {
    dispatch(supportActions.setActiveChat(null))
    dispatch(supportActions.setMessages([]))
    setDraftSeller(null)
    resetComposer()
  }

  const handleSend = async () => {
    const trimmedText = messageText.trim()
    const { images, videos } = attachments
    if ((!trimmedText && !images.length && !videos.length) || (!activeChat && !draftSeller) || isSending) return
    if (trimmedText.length > MESSAGE_MAX_LENGTH) return

    setIsSending(true)
    try {
      if (activeChat) {
        const response = await chatApi.sendSupportMessageAsAdmin(activeChat._id, {
          text: trimmedText
        }, { images, videos })
        dispatch(supportActions.addMessage(response.data))
        dispatch(supportActions.markChatRead(activeChat._id))
      } else if (draftSeller) {
        const { data } = await chatApi.sendFirstSupportMessageToSeller(draftSeller._id, {
          text: trimmedText
        }, { images, videos })
        setDraftSeller(null)
        dispatch(supportActions.setActiveChat(data.chat))
        dispatch(supportActions.setMessages([data.message]))
        dispatch(supportActions.applyChatUpdated({ chat: data.chat }))
      }
      setMessageText('')
      attachments.clear()
    } catch (e) {
      showMessage('error', 'Сообщение не отправлено', getRequestErrorText(e))
    } finally {
      setIsSending(false)
    }
  }

  const canEditMessage = (message: IChatMessage) =>
    message.sender_type === 'SUPPORT' &&
    !message.is_auto_reply &&
    !message.is_internal &&
    Boolean(userId) &&
    message.sender_user_id === userId &&
    isWithinEditWindow(message, now)

  const canMarkRead = (message: IChatMessage) =>
    message.sender_type === 'USER' && message.is_read_by_support === false

  // Сервер возвращает в непрочитанные именно последнее сообщение автора
  const lastAuthorMessageId = React.useMemo(
    () => [...messages].reverse().find((message) => message.sender_type === 'USER')?._id,
    [messages]
  )
  const canMarkUnread = (message: IChatMessage) =>
    message._id === lastAuthorMessageId && message.is_read_by_support === true

  const handleStartEdit = (message: IChatMessage) => {
    if (!editingMessage) {
      draftRef.current = messageText
    }
    setEditingMessage(message)
    setMessageText(message.text)
  }

  const handleCancelEdit = () => {
    setEditingMessage(null)
    setMessageText(draftRef.current)
    draftRef.current = ''
  }

  const handleSaveEdit = async () => {
    const trimmedText = messageText.trim()
    if (!editingMessage || !activeChat?._id || isSending) return
    if (!trimmedText && !hasMedia(editingMessage)) return
    if (trimmedText === editingMessage.text) {
      handleCancelEdit()
      return
    }

    setIsSending(true)
    try {
      const { data } = await chatApi.editSupportMessageAsAdmin(activeChat._id, editingMessage._id, trimmedText)
      dispatch(supportActions.applyMessageUpdated({ chatId: activeChat._id, message: data }))
      handleCancelEdit()
    } catch (e) {
      showMessage('error', 'Сообщение не изменено', getRequestErrorText(e))
    } finally {
      setIsSending(false)
    }
  }

  const isDialogOpen = Boolean(activeChat || draftSeller)
  const isDialogOpenOnMobile = isMobile && isDialogOpen

  const tabItems = [
    {
      key: ESupportChatTab.SELLERS,
      label: (
        <Badge count={counters.sellers} size='small' offset={[10, -2]}>
          <span>Селлеры</span>
        </Badge>
      )
    },
    {
      key: ESupportChatTab.BUYERS,
      label: (
        <Badge count={counters.buyers} size='small' offset={[10, -2]}>
          <span>Покупатели</span>
        </Badge>
      )
    }
  ]

  // Та же очерёдность, что в getChatAuthorName: обращение продавца — на карточку продавца
  const activeChatCardPath = activeChat?.support_seller_id?._id
    ? getSellerEditPath(activeChat.support_seller_id._id)
    : activeChat?.user_id?._id
      ? getUserEditPath(activeChat.user_id._id)
      : draftSeller
        ? getSellerEditPath(draftSeller._id)
        : null
  const dialogTitle = activeChat
    ? getChatAuthorName(activeChat)
    : draftSeller?.shop_details?.shop_name || 'Продавец'
  const dialogSubtitle = activeChat
    ? getSupportChatSubtitle(activeChat)
    : 'Диалог появится после первого сообщения'

  return (
    <div className={`${styles.wrapper} ${styles.outsideSellerWrapper}`} ref={wrapperRef}>
      <div className={`${styles.list} ${isDialogOpenOnMobile ? styles.hidden : ''}`}>
        <div className={styles.listHeader}>
          <Title level={4} style={{ margin: 0 }}>Обращения</Title>
        </div>
        <div className={styles.tabs}>
          <Tabs
            activeKey={tab}
            items={tabItems}
            onChange={(key) => {
              setDraftSeller(null)
              dispatch(supportActions.setTab(key as ESupportChatTab))
            }}
          />
        </div>
        <div className={styles.listItems} ref={listItemsRef}>
          {isChatsLoading && !chats.length ? (
            <div className={styles.empty}><Spin /></div>
          ) : chats.length ? (
            <>
              {chats.map((chat) => (
                <div
                  key={chat._id}
                  className={`${styles.chatItem} ${activeChat?._id === chat._id ? styles.chatItemActive : ''}`}
                  onClick={() => handleSelectChat(chat._id)}
                >
                  <div className={styles.chatItemTop}>
                    <span className={styles.userName}>{getChatAuthorName(chat)}</span>
                    <span className={styles.date}>{formatDateTime(chat.last_message_at)}</span>
                  </div>
                  <div className={styles.lastMessageWrapper}>
                    <p className={styles.lastMessage}>{chat.last_message_text || 'Нет сообщений'}</p>
                    {Boolean(chat.unread_for_support) && (
                      <span className={styles.unreadBadge}>{chat.unread_for_support}</span>
                    )}
                  </div>
                </div>
              ))}
              <div ref={loadMoreRef} className={styles.loadMoreMarker} />
              {isMoreChatsLoading && <div className={styles.moreLoader}><Spin /></div>}
            </>
          ) : (
            <div className={styles.empty}><Empty description='Обращений нет' /></div>
          )}
        </div>
      </div>

      <div className={`${styles.dialog} ${isMobile && !isDialogOpen ? styles.hidden : ''}`}>
        {!isDialogOpen ? (
          <div className={styles.empty}>
            <Empty description='Выберите обращение'>
              {tab === ESupportChatTab.SELLERS && <SupportSellerPicker onSelect={handleOpenSellerChat} />}
            </Empty>
          </div>
        ) : (
          <>
            <div className={styles.dialogHeader}>
              {isMobile && (
                <Button
                  type='text'
                  size='small'
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBackToList}
                  className={styles.backButton}
                >
                  К обращениям
                </Button>
              )}
              <div className={styles.headerInfo}>
                <Title level={4} className={styles.headerTitle}>
                  {activeChatCardPath ? (
                    <CardLink to={activeChatCardPath} newTab>
                      {dialogTitle}
                    </CardLink>
                  ) : (
                    dialogTitle
                  )}
                </Title>
                <Text className={styles.headerSubtitle}>
                  {dialogSubtitle}
                </Text>
              </div>
            </div>
            <div className={styles.messages} ref={messagesRef} onScroll={handleMessagesScroll}>
              {isMessagesLoading ? (
                <Spin />
              ) : messages.length ? (
                messages.map((message) => (
                  <div
                    key={message._id}
                    className={[
                      styles.messageRow,
                      message.is_internal ? styles.internal : '',
                      !message.is_internal && isOwnMessage(message) ? styles.own : ''
                    ].join(' ')}
                  >
                    <div className={styles.bubble}>
                      {message.is_internal && (
                        <span className={styles.internalLabel}>Техническая информация</span>
                      )}
                      {hasMedia(message) && <ChatMessageMedia images={message.images} videos={message.videos} />}
                      {message.text && <p className={styles.messageText}>{message.text}</p>}
                      <ChatMessageMeta
                        message={message}
                        onEdit={canEditMessage(message) ? () => handleStartEdit(message) : undefined}
                        onMarkRead={canMarkRead(message) ? handleMarkRead : undefined}
                        onMarkUnread={canMarkUnread(message) ? handleMarkUnread : undefined}
                      />
                    </div>
                  </div>
                ))
              ) : (
                <Empty description='Сообщений пока нет' />
              )}
            </div>
            {newMessagesCount > 0 && (
              <button
                type='button'
                className={styles.scrollToBottomButton}
                onClick={() => scrollToLatestMessage()}
                aria-label='Перейти к новым сообщениям'
              >
                <img src={chatArrowDownIcon} alt='' />
                <span className={styles.scrollToBottomBadge}>{newMessagesCount > 99 ? '99+' : newMessagesCount}</span>
              </button>
            )}
            <ChatComposer
              value={messageText}
              onChange={setMessageText}
              onSubmit={editingMessage ? handleSaveEdit : handleSend}
              placeholder={draftSeller ? 'Сообщение продавцу' : 'Ответ от поддержки'}
              maxLength={MESSAGE_MAX_LENGTH}
              disabled={isSending}
              isSubmitting={isSending}
              editingText={editingMessage ? getMessagePreview(editingMessage) : null}
              onCancelEdit={handleCancelEdit}
              allowEmptyText={editingMessage ? hasMedia(editingMessage) : attachments.attachments.length > 0}
              attachments={attachments.attachments}
              onAttach={attachments.add}
              onRemoveAttachment={attachments.remove}
              onPaste={attachments.handlePaste}
            />
          </>
        )}
      </div>
    </div>
  )
}
